'use client';

import { ListChecks, Map } from 'lucide-react';
import React from 'react';

// Define default icons as constants to avoid unstable prop warnings
const DEFAULT_MAP_ICON = <Map className="w-6 h-6 text-orange-500" />;
const DEFAULT_LIST_ICON = <ListChecks className="w-6 h-6 text-orange-500" />;

interface ViewToggleFloatingButtonProps {
  onClick: () => void;
  className?: string;
  viewMode?: 'map' | 'list'; // In 'list' mode, button shows mapIcon to go to map. In 'map' mode, button shows listIcon to go to list.
  mapIcon?: React.ReactNode;
  listIcon?: React.ReactNode;
  ariaLabel?: string;
}

/**
 * ViewToggleFloatingButton - A floating action button to toggle between views (e.g., map and list).
 * Designed for mobile screens.
 */
export function ViewToggleFloatingButton({
  onClick,
  className = '',
  viewMode = 'list', // Default to 'list' mode, meaning it shows the mapIcon
  mapIcon = DEFAULT_MAP_ICON, // Icon to show when in 'list' mode (goes to map)
  listIcon = DEFAULT_LIST_ICON, // Icon to show when in 'map' mode (goes to list)
  ariaLabel = 'Toggle view'
}: ViewToggleFloatingButtonProps) {
  const iconToShow = viewMode === 'list' ? mapIcon : listIcon;
  const effectiveAriaLabel = ariaLabel || (viewMode === 'list' ? 'Open map view' : 'Open list view');

  return (
    <button
      type="button"
      onClick={onClick}
      className={`absolute bottom-4 right-4 z-50 bg-white rounded shadow-lg p-3 flex items-center justify-center cursor-pointer md:hidden ${className}`}
      aria-label={effectiveAriaLabel}
    >
      {iconToShow}
    </button>
  );
}
